import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import ArtWorkImage from "./ArtWorkImage";
import { buildMediaUrl } from "@src/utils/mediaUrl";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.18s ease-out;
  pointer-events: none;
`;

const PreviewFrame = styled.div<{ $left: number; $top: number; $size: number }>`
  position: fixed;
  left: ${({ $left }) => `${$left}px`};
  top: ${({ $top }) => `${$top}px`};
  width: ${({ $size }) => `${$size}px`};
  height: ${({ $size }) => `${$size}px`};
  z-index: 9999;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.yellow};
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
  animation: ${scaleIn} 0.2s ease-out;
  pointer-events: none;
  background-color: ${({ theme }) => theme.colors.darkBg};
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Trigger = styled.span<{ $text?: boolean; $interactive?: boolean }>`
  display: inline-flex;
  line-height: ${({ $text }) => ($text ? "inherit" : 0)};
  /* Only promise a zoom when there is actually an image to preview: without one
     the hover is a no-op, so a zoom-in cursor would be an empty affordance. */
  cursor: ${({ $interactive }) => ($interactive ? "zoom-in" : "inherit")};
`;

type ArtWorkImageHoverPreviewProps = {
  value?: string | null;
  $compressedUI?: boolean;
  customSize?: string;
  alt?: string;
  children?: React.ReactNode;
};

const PREVIEW_SIZE = 320;
const PREVIEW_GAP = 16;
const VIEWPORT_PADDING = 16;

const ArtWorkImageHoverPreview = ({
  value,
  $compressedUI,
  customSize,
  alt = "Playlist cover preview",
  children,
}: ArtWorkImageHoverPreviewProps): React.JSX.Element => {
  const [previewPosition, setPreviewPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const imageUrl = buildMediaUrl(value);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      if (!imageUrl) return;

      const rect = event.currentTarget.getBoundingClientRect();
      let left = rect.right + PREVIEW_GAP;
      let top = rect.top + rect.height / 2 - PREVIEW_SIZE / 2;

      if (left + PREVIEW_SIZE > window.innerWidth - VIEWPORT_PADDING) {
        left = rect.left - PREVIEW_GAP - PREVIEW_SIZE;
      }

      top = Math.max(
        VIEWPORT_PADDING,
        Math.min(top, window.innerHeight - PREVIEW_SIZE - VIEWPORT_PADDING)
      );

      left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, window.innerWidth - PREVIEW_SIZE - VIEWPORT_PADDING)
      );

      setPreviewPosition({ left, top });
    },
    [imageUrl]
  );

  const handleMouseLeave = useCallback(() => {
    setPreviewPosition(null);
  }, []);

  return (
    <>
      <Trigger
        $text={!!children}
        $interactive={!!imageUrl}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children ?? (
          <ArtWorkImage
            $compressedUI={$compressedUI}
            customSize={customSize}
            value={imageUrl}
          />
        )}
      </Trigger>
      {previewPosition &&
        createPortal(
          <>
            <Backdrop />
            <PreviewFrame
              $left={previewPosition.left}
              $top={previewPosition.top}
              $size={PREVIEW_SIZE}
            >
              <PreviewImage src={imageUrl} alt={alt} />
            </PreviewFrame>
          </>,
          document.body
        )}
    </>
  );
};

export default ArtWorkImageHoverPreview;
